import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { LicenseInfoService } from 'shared/organization-info/license-info.service';
import { filter, switchMap, take, forkJoin } from 'rxjs';
import { RedirectInfo, LicenseRedirectPopupData } from './license-redirect.types';
import { LicenseRedirectPopupComponent } from '../license-redirect-popup/license-redirect-popup.component';
import { OrganizationInfoService } from 'shared/organization-info/organization-info.service';
import { AuthService } from './auth.service';
import { IamOrganizationsApiService } from '@kempoweroyj/ngx-iam-organizations-api-client';
import { LicensingApiService } from '@kempoweroyj/ngx-licensing-api-client';
@Injectable({
    providedIn: 'root',
})
export class LicenseRedirectService {
    constructor(
        private readonly licenseInfoService: LicenseInfoService,
        private readonly dialog: MatDialog,
        private readonly organizationInfoService: OrganizationInfoService,
        private readonly authService: AuthService,
        private readonly iamOrganizationsApi: IamOrganizationsApiService,
        private readonly licensingApi: LicensingApiService
    ) {}

    /**
     * Simple redirect check using existing license info
     * @returns Promise<boolean> - true if redirect occurred, false otherwise
     */
    checkAndRedirectBasedOnLicense(): Promise<boolean> {
        return new Promise((resolve) => {
            // Wait for license info to be available and check redirect requirement
            this.licenseInfoService.licenseInfo$
                .pipe(
                    filter((license) => license !== null), // Wait until we have license data
                    switchMap(() =>
                        forkJoin({
                            redirectInfo: this.licenseInfoService.getRedirectRequirement$().pipe(take(1)),
                            userTenancyAccess: this.organizationInfoService.userTenancyAccess$.pipe(
                                filter((access) => access !== null),
                                take(1)
                            ),
                        })
                    ),
                    take(1)
                )
                .subscribe(async ({ redirectInfo }) => {
                    if (redirectInfo.shouldRedirect) {
                        // Check if user has access to ChargEye organizations
                        const hasChargeyeAccess = await this.checkUserHasAccessToChargeyeOrganization();

                        if (hasChargeyeAccess) {
                            // User has access to a ChargEye organization - don't show redirect popup
                            resolve(false);
                            return;
                        }

                        // User only has MyKempower access - show redirect popup
                        const dialogRef = this.showRedirectPopup(redirectInfo);
                        // Wait for dialog to close (user will either redirect or logout)
                        dialogRef.afterClosed().subscribe(() => {
                            resolve(false);
                        });
                    } else {
                        resolve(false); // No redirect needed
                    }
                });
        });
    }

    private performRedirect(): void {
        const currentUrl = globalThis.location.href;
        const newUrl = this.buildRedirectUrl(currentUrl);

        if (newUrl !== currentUrl) {
            globalThis.location.href = newUrl;
        }
    }

    private showRedirectPopup(redirectInfo: RedirectInfo): MatDialogRef<LicenseRedirectPopupComponent> {
        const dialogData: LicenseRedirectPopupData = { redirectInfo };
        return LicenseRedirectPopupComponent.open(this.dialog, dialogData);
    }

    /**
     * Public method to perform redirect from popup confirmation
     * redirects to MyKempower
     */
    performRedirectFromPopup(): void {
        this.performRedirect();
    }

    /**
     * Check if user has access to any organization with ChargEye subscription
     * @returns Promise<boolean> - true if user has access to ChargEye organization
     */
    async checkUserHasAccessToChargeyeOrganization(): Promise<boolean> {
        const userTenancyAccess = this.organizationInfoService.userTenancyAccess;

        if (!userTenancyAccess || userTenancyAccess.length === 0) {
            return false;
        }

        let hasChargeyeAccess = false;

        for (const access of userTenancyAccess) {
            try {
                const impersonateResult = await this.iamOrganizationsApi.impersonate({
                    tenantId: access.targetTenantId,
                    systemRoles: access.systemRoles as any,
                });

                if (impersonateResult?.token) {
                    const licensesResponse = await this.licensingApi
                        .withToken(impersonateResult.token)
                        .getActiveLicenses();
                    const licenses = licensesResponse?.licenses || [];

                    const hasChargeyeLicense = licenses.some((l) => l.type.startsWith('chargeye.'));
                    if (hasChargeyeLicense) {
                        hasChargeyeAccess = true;
                        break; // Found ChargEye access, no need to check more organizations
                    }
                }
            } catch (error) {
                console.warn('[LicenseRedirect] Failed to check organization access:', error);
                continue;
            }
        }

        return hasChargeyeAccess;
    }

    private buildRedirectUrl(currentUrl: string): string {
        try {
            const url = new URL(currentUrl);

            //Skip redirect on localhost
            if (url.hostname === 'localhost') {
                return currentUrl;
            }

            const targetDomain = this.buildTargetDomain(url.hostname);

            return `${url.protocol}//${targetDomain}`;
        } catch (error) {
            // If URL parsing fails, return the current URL to prevent redirect
            console.warn('Failed to parse URL for redirect:', currentUrl, error);
            return currentUrl;
        }
    }

    private buildTargetDomain(hostname: string): string {
        // Parse environment from hostname prefix
        let envPrefix = '';
        if (hostname.startsWith('dev.')) envPrefix = 'dev.';
        else if (hostname.startsWith('test.')) envPrefix = 'test.';

        // Always redirect to MyKempower
        return `${envPrefix}mykempower.com`;
    }
}
